/**
 * QUIPWA — promise-based dialogs
 *
 * `confirmDialog` replaces `window.confirm`: styled, translated, accessible,
 * and able to offer more than yes/no.
 *
 * ```ts
 * const choice = await confirmDialog({
 *   title: t('account.signOut.confirmTitle'),
 *   body: t('account.signOut.confirmBody'),
 *   actions: [
 *     { id: 'keep', label: t('account.signOut.keep') },
 *     { id: 'wipe', label: t('account.signOut.wipe'), tone: 'danger' },
 *   ],
 * })
 * if (choice === 'wipe') …        // null = cancelled (Esc, backdrop, Cancel)
 * ```
 */
import { create } from 'zustand'

export type DialogTone = 'default' | 'primary' | 'danger'

export interface DialogAction {
  id: string
  label: string
  tone?: DialogTone
}

export interface DialogRequest {
  title: string
  body?: string
  /** Defaults to a single primary "Confirm". */
  actions?: DialogAction[]
  cancelLabel?: string
}

interface PendingDialog extends DialogRequest {
  id: number
  resolve: (actionId: string | null) => void
}

interface DialogState {
  current: PendingDialog | null
  queue: PendingDialog[]
  open: (request: DialogRequest) => Promise<string | null>
  settle: (actionId: string | null) => void
}

let nextId = 1

export const useDialogs = create<DialogState>((set, get) => ({
  current: null,
  queue: [],
  open: (request) =>
    new Promise<string | null>((resolve) => {
      const pending: PendingDialog = { ...request, id: nextId++, resolve }
      if (get().current) set((s) => ({ queue: [...s.queue, pending] }))
      else set({ current: pending })
    }),
  settle: (actionId) => {
    const { current, queue } = get()
    if (!current) return
    current.resolve(actionId)
    const [next, ...rest] = queue
    set({ current: next ?? null, queue: rest })
  },
}))

export function confirmDialog(request: DialogRequest): Promise<string | null> {
  return useDialogs.getState().open(request)
}

/** Yes/no shorthand: resolves true only when the user confirms. */
export async function confirmAction(request: Omit<DialogRequest, 'actions'> & { confirmLabel: string; danger?: boolean }): Promise<boolean> {
  const { confirmLabel, danger, ...rest } = request
  const choice = await confirmDialog({ ...rest, actions: [{ id: 'confirm', label: confirmLabel, tone: danger ? 'danger' : 'primary' }] })
  return choice === 'confirm'
}
